import React, { useState, useEffect, useRef } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import StandardButton from '../core/StandardButton';
import ConfirmationModal from '../core/ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import UnifiedLocationSelector from '../locality/UnifiedLocationSelector';
import LanguageSelector from '../core/LanguageSelector';
import { Upload, ChevronDown, Layers, Plus, X, ClipboardList } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import ImageCropper from '../core/ImageCropper/ImageCropper';
import { getChildGroups, getFederationMembers, getEligibleParentGroups, getFullGroupTree, getTreeDescendantCount, MAX_TREE_DESCENDANTS_FREE } from '../../utils/groupHelpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Import protected Supabase client and direct fetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';
import { groupService } from '../../data/dataService';
import { tokens } from '../../styles/designTokens';
import { updateListing, syncLinkedListing } from '../../data/directoryService';
import ReconcileLinkModal from '../user/ReconcileLinkModal';
import { addBreadcrumb, addCaughtError } from '../../utils/errorCapture';
import { GROUP_NAME_MAX, GROUP_DESCRIPTION_MAX, ARTICLE_IMAGE_CAPTION_MAX } from '../../utils/validationConstants';
import { generateGroupTagPrompt } from '../../utils/aiPromptTemplates';
import GroupPreviewModal from './GroupPreviewModal';
import GroupClosingBanner from './GroupClosingBanner';
import { useGroupClosing } from '../../hooks/useGroupClosing';
import { isRateLimited, rateLimitMessage, RATE_LIMIT_TITLE_KEY } from '../../utils/rateLimitMessage';

// Auth token helper - uses supabaseFetch.getAuthToken() which auto-refreshes expired JWTs
const getAuthToken = () => supabaseFetch.getAuthToken();

// The three group types (addendum 5), each with one plain line (R6). groups:group_type_* keys.
const GROUP_TYPE_OPTIONS = [
  { value: 'public', labelKey: 'group_type_public', hintKey: 'group_type_public_hint' },
  { value: 'private_findable', labelKey: 'group_type_private_findable', hintKey: 'group_type_private_findable_hint' },
  { value: 'private_invitation', labelKey: 'group_type_private_invitation', hintKey: 'group_type_private_invitation_hint' }
];

const AddEditGroup = ({ userId, onBack, currentContext, mode = 'add', group = null, parentGroup = null, prefill = null }) => {
  // Editing a closing group (Roadmap #103, brief ruling 4): the form is shut — the notice, with
  // Undo for the owner, instead of its controls; Delete is not offered a second time.
  const closingState = useGroupClosing(mode === 'edit' ? group?.id || null : null, userId ? { id: userId } : null);
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('groups');
  const { isMobile } = useMediaQuery();
  const [loading, setLoading] = useState(false);
  // Group privacy lock (docs/briefs/2026-09-27-group-privacy-lock.md): once people other than the
  // owner have posted, the Public/Private choice is shown but disabled. Fails open — the database
  // guard is authoritative.
  const [privacyLocked, setPrivacyLocked] = useState(false);
  // The type as saved (edit only), for the privacy lock; and whether this is a Public group that
  // still approves new members — the two groups addendum 6 left with approval. Fully Public now
  // means anyone joins (brief 2026-10-04 R2), so such a group is shown a notice and must choose.
  const [savedAccess, setSavedAccess] = useState(null);
  const [legacyApproval, setLegacyApproval] = useState(false);

  // Logo click listener - navigate back to MainFeed
  // Asks first when there are unsaved changes (#78); confirmLeaveRef is set once the hook below runs.
  const confirmLeaveRef = useRef(null);
  useEffect(() => {
    const handleLogoClick = async () => {
      const leave = confirmLeaveRef.current ? await confirmLeaveRef.current() : true;
      if (leave && onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);
  const [uploading, setUploading] = useState(false);
  const [selectedBoundaryIds, setSelectedBoundaryIds] = useState([]);
  // Directory→group link intent carried in from a prefill; consumed once on successful create.
  const pendingLinkRef = useRef(null);
  // When a create-from-prefill group diverges from its source listing, hold the reconciliation
  // until the Steward resolves it: null | { listing, group, groupName }.
  const [reconcileAfterCreate, setReconcileAfterCreate] = useState(null);

  // Show the create-success modal, then route to the group's editable landing page — the
  // setup-end handoff (functional-fallback build brief §4): every new owner is shown their
  // public face and invited, not forced, to improve it before landing on admin-group (still the
  // "done" destination if they skip). Shared by the normal create path and the deferred
  // reconciliation completion.
  const navigateAfterCreate = (newGroupId, name) => {
    const groupObject = {
      id: newGroupId,
      name,
      is_private: formData.is_private,
      auto_accept_members: formData.auto_accept_members,
      group_access: formData.group_access
    };
    // Fully Public: the Introduction is optional (a public group's invitations land on it only if
    // it exists), so it is offered, and "Not now" goes on to the group's admin page — the "done"
    // destination the editor itself leads to (addendum 1 item 4).
    if (formData.group_access === 'public') {
      setModal({
        visible: true,
        message: t('success_group_created_public'),
        okLabel: t('intro_write'),
        cancelLabel: t('intro_not_now'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          window.dispatchEvent(new CustomEvent('ournet:navigate', {
            detail: { view: 'group-landing', group: groupObject, postCreate: true }
          }));
        },
        onCancel: () => {
          setModal({ visible: false, message: '', onOk: null });
          window.dispatchEvent(new CustomEvent('ournet:navigate', {
            detail: { view: 'admin-group', group: groupObject, postCreate: true }
          }));
        }
      });
      return;
    }
    setModal({
      visible: true,
      message: t('success_group_created'),
      onOk: () => {
        setModal({ visible: false, message: '', onOk: null });
        window.dispatchEvent(new CustomEvent('ournet:navigate', {
          detail: {
            view: 'group-landing',
            // is_private / auto_accept_members carried through from the form the caller just
            // submitted — AdminGroup's Smart Link card gates on both (Smart Links Phase 2, S5)
            // and an undefined value there reads as "public, auto-accept", which is wrong for a
            // group just created private. Still needed here since skipping the landing-page
            // handoff routes on to admin-group with this same group object.
            group: {
              id: newGroupId,
              name,
              is_private: formData.is_private,
              auto_accept_members: formData.auto_accept_members,
              group_access: formData.group_access
            },
            postCreate: true
          }
        }));
      }
    });
  };
  const [userLongitude] = useState(-90); // Default to Americas

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    group_type: 'private', // 'public' or 'private' — follows group_access
    // The group's type (addendum 5): 'public' | 'private_findable' | 'private_invitation'. A new
    // group has none preselected: the owner must choose (Architect, 4 Oct 2026). Edit loads it.
    group_access: '',
    cover_image_url: '',
    image_caption: '',
    postcode: '',
    is_private: true, // corresponds to group_type
    auto_accept_members: false, // Public: the owner's "approve new members" choice (addendum 6); private: always false
    // Nested Groups fields
    parent_group_id: null, // UUID of parent group (null for top-level)
    visible_to_parent: false, // Whether subgroup is visible to parent group members
    // Tags
    community_tags: [], // Array of tag strings
    // Boundary IDs (Portugal administrative boundaries)
    boundary_ids: [], // Array of boundary IDs from admin_boundaries table
    // Federation
    is_federation: false, // True = umbrella federation group with member groups
    federation_application_schema: [], // Array of { key, label, type, required } — Federator's required fields
    // Contact
    show_contact_info: false,
    contact_details: ''
  });

  const [imagePreview, setImagePreview] = useState(null);
  // #78: true once the form holds what it opened with (edit: the saved group; add: any prefill).
  const [formLoaded, setFormLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [tagInput, setTagInput] = useState('');
  const [landingPageContent, setLandingPageContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // NEW - Image Cropper state
  const [showGroupCropper, setShowGroupCropper] = useState(false);
  const [groupImageDisplay, setGroupImageDisplay] = useState('');
  const [groupImageThumbnail, setGroupImageThumbnail] = useState('');

  // Nested Groups state - selectedParentGroup comes from prop or edit mode
  const [selectedParentGroup, setSelectedParentGroup] = useState(null);

  // Group owner ID (fetched fresh in edit mode, may not be in the prop for sub-groups)
  const [groupOwnerId, setGroupOwnerId] = useState(group?.owner_id || null);

  // Child groups state (for edit mode - showing existing sub-groups)
  const [childGroups, setChildGroups] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [currentGroupHierarchyLevel, setCurrentGroupHierarchyLevel] = useState(1);

  // Federation members state (for edit mode - showing member groups).
  // Two lists, not one (Federation 4C §6): fedMembers is filtered (deleted_at IS NULL) for
  // display in the section below; fedMembersUnfiltered is unfiltered and feeds only the
  // un-designation guard (handleFederationToggle), which needs to see a dangling pointer.
  const [fedMembers, setFedMembers] = useState([]);
  const [fedMembersUnfiltered, setFedMembersUnfiltered] = useState([]);
  // Pending federation applications (addendum 1 of the federation-status-guard brief): the
  // database also refuses un-designation while any is waiting. A convenience count under the
  // owner's own read rights — the database guard is the authority for anything it cannot see.
  const [fedPendingCount, setFedPendingCount] = useState(0);
  const [loadingFedMembers, setLoadingFedMembers] = useState(false);

  // Parent Group Selector state (for 'add' mode only)
  const [eligibleParentGroups, setEligibleParentGroups] = useState([]);
  const [loadingEligibleParents, setLoadingEligibleParents] = useState(false);
  const [parentSelectorOpen, setParentSelectorOpen] = useState(false);

  // Group Tree Visualization state (text-only hierarchy)
  const [groupTree, setGroupTree] = useState(null);
  const [loadingGroupTree, setLoadingGroupTree] = useState(false);

  // Page title
  const pageTitle = mode === 'edit' ? t('edit_group') : t('create_group');
  // New group: what comes after saving (addendum 1 item 4) — the Introduction editor opens for a
  // private type, and is offered for Fully Public.
  const introNextLine = mode !== 'edit' && formData.group_access
    ? (formData.group_access === 'public' ? t('intro_next_public') : t('intro_next_private'))
    : null;
  const headerContext = currentContext && typeof currentContext === 'object'
    ? { ...currentContext, name: pageTitle }
    : { name: pageTitle };

  // If opened in edit mode, fetch fresh data and populate form
  useEffect(() => {
    if (mode !== 'edit' || !group?.id) return;
    let cancelled = false;
    // A failed lock check must never break the form: leave the choice enabled and let the
    // database guard refuse. The async wrapper also catches a synchronous throw.
    const loadPrivacyLock = async () => {
      try {
        const locked = await groupService.getGroupPrivacyLocked(group.id);
        if (!cancelled) setPrivacyLocked(locked === true);
      } catch (err) {
        console.warn('Group privacy lock check failed; leaving the choice enabled:', err);
        addCaughtError(err?.message || String(err), 'AddEditGroup:privacyLock');
        if (!cancelled) setPrivacyLocked(false);
      }
    };
    loadPrivacyLock();
    return () => { cancelled = true; };
  }, [mode, group?.id]);

  useEffect(() => {
    if (mode !== 'edit' || !group || !group.id) return;

    const loadGroupData = async () => {
      try {
        // Fetch fresh group data from database (direct fetch, bypasses JS client)
        // Note: Using explicit select to handle missing boundary_ids column gracefully
        const { data: freshGroup, error } = await supabaseFetch.query('communities', {
          select: 'id,name,description,community_type,is_private,auto_accept_members,group_access,slug,logo_url,cover_image_url,owner_id,member_count,article_count,postcode,group_radius,admin_level,created_at,updated_at,parent_group_id,hierarchy_level,visible_to_parent,pricing_tier,max_members,image_caption,hero_image_display_url,hero_image_thumbnail_url,is_federation,federation_application_schema,show_contact_info,contact_details',
          eq: { id: group.id },
          single: true
        });

        // Try to get boundary_ids separately (will fail gracefully if column doesn't exist)
        let boundaryIds = [];
        try {
          const { data: boundaryData } = await supabaseFetch.query('communities', {
            select: 'boundary_ids',
            eq: { id: group.id },
            single: true
          });
          boundaryIds = boundaryData?.boundary_ids || [];
        } catch (err) {
          console.log('boundary_ids column not yet available (migration not applied)');
        }

        // Add boundary_ids to freshGroup data
        if (freshGroup) {
          freshGroup.boundary_ids = boundaryIds;
        }

        if (error) throw error;

        console.log('Loading fresh group data for edit:', freshGroup);

        // Store owner_id from fresh data (prop may not have it for sub-groups)
        if (freshGroup.owner_id) setGroupOwnerId(freshGroup.owner_id);

        // Use hero_image_display_url first (modern ImageCropper), then cover_image_url, then logo_url
        const imageUrl = freshGroup.hero_image_display_url || freshGroup.cover_image_url || freshGroup.logo_url || '';

        const loadedAccess = freshGroup.group_access || (freshGroup.is_private ? 'private_invitation' : 'public');
        const loadedLegacyApproval = loadedAccess === 'public' && freshGroup.auto_accept_members === false;

        // Map group fields into formData
        const mapped = {
          name: freshGroup.name || '',
          description: freshGroup.description || '',
          group_type: freshGroup.is_private ? 'private' : 'public',
          // A Public group that still approves new members has no preselected type: its owner
          // chooses Fully Public (opens it) or Private – Request to join (keeps approval).
          group_access: loadedLegacyApproval ? '' : loadedAccess,
          cover_image_url: imageUrl,
          image_caption: freshGroup.image_caption || '',
          postcode: freshGroup.postcode || '',
          is_private: freshGroup.is_private !== undefined ? freshGroup.is_private : true,
          auto_accept_members: freshGroup.auto_accept_members !== undefined ? freshGroup.auto_accept_members : false,
          boundary_ids: freshGroup.boundary_ids || [],
          is_federation: freshGroup.is_federation || false,
          federation_application_schema: Array.isArray(freshGroup.federation_application_schema)
            ? freshGroup.federation_application_schema
            : [],
          show_contact_info: freshGroup.show_contact_info || false,
          contact_details: freshGroup.contact_details || '',
        };

        console.log('Mapped form data:', mapped);
        console.log('Image URL:', imageUrl);
        console.log('Boundary IDs:', freshGroup.boundary_ids);

        setFormData(prev => ({ ...prev, ...mapped }));
        setSavedAccess(loadedAccess);
        setLegacyApproval(loadedLegacyApproval);
        setSelectedBoundaryIds(freshGroup.boundary_ids || []);
        if (imageUrl) {
          console.log('Setting image preview to:', imageUrl);
          setImagePreview(imageUrl);
        }

        // Restore modern image URLs so saves don't lose them
        if (freshGroup.hero_image_display_url) {
          setGroupImageDisplay(freshGroup.hero_image_display_url);
        }
        if (freshGroup.hero_image_thumbnail_url) {
          setGroupImageThumbnail(freshGroup.hero_image_thumbnail_url);
        }

        // Load community tags
        const { data: tagsData, error: tagsError } = await supabaseFetch.query('community_tags', {
          select: 'tag',
          eq: { community_id: freshGroup.id }
        });

        if (!tagsError && tagsData) {
          const tags = tagsData.map(t => t.tag);
          setFormData(prev => ({ ...prev, community_tags: tags }));
        }

        // Load landing page content for AI prompt
        const { data: landingData, error: landingError } = await supabaseFetch.query('community_landing', {
          select: 'content',
          eq: { community_id: freshGroup.id, is_published: true },
          single: true
        });

        if (!landingError && landingData?.content) {
          setLandingPageContent(landingData.content);
        }

        // Load parent group info if editing a subgroup
        if (freshGroup.parent_group_id) {
          const { data: parentGroup } = await supabaseFetch.query('communities', {
            select: 'id, name, hierarchy_level, logo_url',
            eq: { id: freshGroup.parent_group_id },
            single: true
          });

          if (parentGroup) {
            setSelectedParentGroup(parentGroup);
            setFormData(prev => ({
              ...prev,
              parent_group_id: parentGroup.id,
              visible_to_parent: freshGroup.visible_to_parent || false
            }));
          }
        }
      } catch (err) {
        console.error('Error loading group data:', err);
      }
      setFormLoaded(true);
    };

    loadGroupData();
  }, [mode, group]);

  // Initialize parent group from prop (for "Create Sub-Group" flow)
  useEffect(() => {
    if (parentGroup && mode === 'add') {
      setSelectedParentGroup(parentGroup);
      setFormData(prev => ({
        ...prev,
        parent_group_id: parentGroup.id,
        visible_to_parent: false
      }));
    }
  }, [parentGroup, mode]);

  // Seed form state from a directory→group prefill (add mode only). The prefill arrives
  // already in group shape (name, description, boundary_ids, community_tags); AddEditGroup
  // stays generic and just merges it. The link intent is stashed for post-create write-back.
  useEffect(() => {
    if (!prefill || mode !== 'add') return;
    setFormData(prev => ({
      ...prev,
      ...(prefill.name !== undefined ? { name: prefill.name || '' } : {}),
      ...(prefill.description !== undefined ? { description: prefill.description || '' } : {}),
      ...(Array.isArray(prefill.boundary_ids) ? { boundary_ids: prefill.boundary_ids } : {}),
      ...(Array.isArray(prefill.community_tags) ? { community_tags: prefill.community_tags } : {})
    }));
    if (Array.isArray(prefill.boundary_ids)) {
      setSelectedBoundaryIds(prefill.boundary_ids);
    }
    // Stash the link intent plus a snapshot of the listing's original values (the prefill
    // source), so a post-create reconciliation can diff them against any Steward edits.
    pendingLinkRef.current = prefill.link
      ? {
          ...prefill.link,
          original: {
            name: prefill.name || '',
            description: prefill.description || '',
            boundary_id: Array.isArray(prefill.boundary_ids) ? (prefill.boundary_ids[0] ?? null) : null
          }
        }
      : null;
  }, [prefill, mode]);

  // Load child groups when editing an existing group
  useEffect(() => {
    if (mode === 'edit' && group && group.id) {
      const loadChildren = async () => {
        setLoadingChildren(true);
        try {
          // Get hierarchy level from fresh data (direct fetch)
          const { data: freshGroup } = await supabaseFetch.query('communities', {
            select: 'hierarchy_level',
            eq: { id: group.id },
            single: true
          });

          if (freshGroup) {
            setCurrentGroupHierarchyLevel(freshGroup.hierarchy_level || 1);
          }

          // Load child groups
          const children = await getChildGroups(group.id);
          setChildGroups(children || []);
        } catch (err) {
          console.error('Error loading child groups:', err);
          setChildGroups([]);
        } finally {
          setLoadingChildren(false);
        }
      };
      loadChildren();

      // Load federation members if this is a federation group. Two fetches (4C §6): filtered
      // for the display list below, unfiltered for the un-designation guard's count.
      const loadFedMembers = async () => {
        setLoadingFedMembers(true);
        try {
          const [members, allMembers, pending] = await Promise.all([
            getFederationMembers(group.id),
            // Guard only needs a count — narrow select, not the full display column list.
            getFederationMembers(group.id, { includeDeleted: true, select: 'id' }),
            groupService.countPendingFederationApplications(group.id)
          ]);
          setFedMembers(members || []);
          setFedMembersUnfiltered(allMembers || []);
          setFedPendingCount(pending || 0);
        } catch (err) {
          console.error('Error loading federation members:', err);
          setFedMembers([]);
          setFedMembersUnfiltered([]);
          setFedPendingCount(0);
        } finally {
          setLoadingFedMembers(false);
        }
      };
      // Only load if we know it's a federation (from the group prop)
      if (group.is_federation) {
        loadFedMembers();
      }
    }
  }, [mode, group]);

  // #78: in add mode the form is "loaded" once the effects above have applied any prefill or parent
  // (this effect runs after them in the same commit, so both land in one render).
  useEffect(() => {
    if (mode === 'add') setFormLoaded(true);
  }, [mode]);

  // #78 items 4–5: "unsaved changes" means changed since the form loaded.
  const { confirmLeave, markSaved, dialog: leaveDialog } = useUnsavedChanges({
    value: {
      formData, groupImageDisplay, groupImageThumbnail, selectedBoundaryIds,
      parentId: selectedParentGroup?.id || null
    },
    ready: formLoaded,
    onLeave: () => { if (onBack) onBack(); },
  });
  confirmLeaveRef.current = confirmLeave;

  // Load eligible parent groups when in 'add' mode (only if not already from Create Sub-Group flow)
  useEffect(() => {
    if (mode !== 'add' || parentGroup) return; // Don't load if editing or if parent already provided

    const loadEligibleParents = async () => {
      setLoadingEligibleParents(true);
      try {
        const eligible = await getEligibleParentGroups(userId);
        setEligibleParentGroups(eligible || []);
      } catch (err) {
        console.error('Error loading eligible parent groups:', err);
        setEligibleParentGroups([]);
      } finally {
        setLoadingEligibleParents(false);
      }
    };

    loadEligibleParents();
  }, [mode, parentGroup, userId]);

  // Load full group tree when selectedParentGroup changes OR when editing a group
  // This shows the complete hierarchy for context
  useEffect(() => {
    const loadGroupTreeData = async () => {
      // Determine which group ID to use for loading the tree
      // In edit mode: use the group's own ID (finds root, builds full tree)
      // In add mode with parent selected: use the parent's ID
      const treeGroupId = (mode === 'edit' && group?.id)
        ? group.id
        : selectedParentGroup?.id;

      if (!treeGroupId) {
        setGroupTree(null);
        return;
      }

      setLoadingGroupTree(true);
      try {
        const tree = await getFullGroupTree(treeGroupId);
        setGroupTree(tree);
      } catch (err) {
        console.error('Error loading group tree:', err);
        setGroupTree(null);
      } finally {
        setLoadingGroupTree(false);
      }
    };

    loadGroupTreeData();
  }, [selectedParentGroup, mode, group]);

  // Old locality functions removed - now using boundary_ids with LocationSelector

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // The three types (addendum 5). Once others have posted, the privacy lock fixes public vs
  // private; moving between the two private types is still allowed (it changes no one's access to
  // content, and the database lock only fires when is_private changes).
  const isTypeLocked = (type) => privacyLocked && !!savedAccess && ((type === 'public') !== (savedAccess === 'public'));

  const handleGroupTypeChange = (type) => {
    if (isTypeLocked(type)) return;
    if (errors.group_access) setErrors(prev => ({ ...prev, group_access: null }));
    setFormData(prev => ({
      ...prev,
      group_access: type,
      group_type: type === 'public' ? 'public' : 'private',
      is_private: type !== 'public',
      // Fully Public means anyone joins straight away (brief 2026-10-04 R2: the approval switch has
      // left the form). Private types never admit anyone directly.
      auto_accept_members: type === 'public'
    }));
  };

  // Un-designating a federation with live member groups would orphan every row whose
  // federation_id points here — refuse outright rather than silently allowing it.
  // Counts fedMembersUnfiltered, not fedMembers (4C §6): this guard's job is integrity, not
  // display — it must see a soft-deleted member too, or un-designation would succeed while a
  // dangling federation_id is left behind.
  const handleFederationToggle = (checked) => {
    if (!checked && fedMembersUnfiltered.length > 0) {
      setErrors(prev => ({
        ...prev,
        is_federation: t('federation_undesignate_blocked', { count: fedMembersUnfiltered.length })
      }));
      return;
    }
    if (!checked && fedPendingCount > 0) {
      setErrors(prev => ({
        ...prev,
        is_federation: t('federation_undesignate_pending', { count: fedPendingCount })
      }));
      return;
    }
    setFormData(prev => ({ ...prev, is_federation: checked }));
    if (errors.is_federation) {
      setErrors(prev => ({ ...prev, is_federation: null }));
    }
  };

  // Application-requirements form builder (federation_application_schema).
  // `key` is assigned once, on add, and never regenerated — it is how an applicant's
  // answer (group_join_requests.request_data) stays matched to its question after the
  // Federator later rewords the label. Do not derive it from the label or the row's position.
  const handleAddRequirementField = () => {
    setFormData(prev => ({
      ...prev,
      federation_application_schema: [
        ...(prev.federation_application_schema || []),
        { key: crypto.randomUUID(), label: '', type: 'text', required: false }
      ]
    }));
  };

  const handleUpdateRequirementField = (key, patch) => {
    setFormData(prev => ({
      ...prev,
      federation_application_schema: (prev.federation_application_schema || []).map(f =>
        f.key === key ? { ...f, ...patch } : f
      )
    }));
  };

  // Changing type to email/phone prefills the label with the translated type name — but only
  // into an EMPTY label, never overwriting one the Federator already typed (Michael's ruling).
  // text/longtext get no default. Reuses the existing field_type_email/field_type_phone
  // strings (already the option labels in the type <select>) rather than new keys.
  const handleRequirementTypeChange = (key, newType) => {
    setFormData(prev => ({
      ...prev,
      federation_application_schema: (prev.federation_application_schema || []).map(f => {
        if (f.key !== key) return f;
        const patch = { type: newType };
        if (!(f.label || '').trim()) {
          if (newType === 'email') patch.label = t('field_type_email');
          else if (newType === 'phone') patch.label = t('field_type_phone');
        }
        return { ...f, ...patch };
      })
    }));
  };

  const handleRemoveRequirementField = (key) => {
    setFormData(prev => ({
      ...prev,
      federation_application_schema: (prev.federation_application_schema || []).filter(f => f.key !== key)
    }));
  };

  // Types of any requirement rows currently missing a label — these are exactly what the save
  // filter below would silently drop. Named by type since the label (the only thing that would
  // otherwise identify the row to the Federator) is precisely what's missing (Federation 4A fix:
  // fields-dropped-on-save — the save reported success while discarding rows with no signal).
  const getUnlabelledRequirementTypes = () => (
    (formData.federation_application_schema || [])
      .filter(f => !f.label || !f.label.trim())
      .map(f => t(`field_type_${f.type || 'text'}`))
  );

  // Handle parent group selection from dropdown
  const handleParentGroupSelect = (parentGroupId) => {
    if (!parentGroupId) {
      // "None" selected - clear parent
      setSelectedParentGroup(null);
      setFormData(prev => ({
        ...prev,
        parent_group_id: null,
        visible_to_parent: false
      }));
    } else {
      // Find the selected group from eligible list
      const selected = eligibleParentGroups.find(g => g.id === parentGroupId);
      if (selected && !selected.is_full) {
        setSelectedParentGroup(selected);
        setFormData(prev => ({
          ...prev,
          parent_group_id: selected.id,
          visible_to_parent: false
        }));
      }
    }
    setParentSelectorOpen(false);
  };

  // Clear parent selection
  const handleClearParent = () => {
    setSelectedParentGroup(null);
    setFormData(prev => ({
      ...prev,
      parent_group_id: null,
      visible_to_parent: false
    }));
  };

  // Group image save handler (from ImageCropper)
  const handleGroupImageSave = ({ displayUrl, thumbnailUrl, aspectRatio }) => {
    setGroupImageDisplay(displayUrl);
    setGroupImageThumbnail(thumbnailUrl);
    setImagePreview(displayUrl);
    setFormData(prev => ({ ...prev, cover_image_url: displayUrl }));
    setErrors(prev => ({ ...prev, image: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('error_name_required');
    } else if (formData.name.length > GROUP_NAME_MAX) {
      newErrors.name = t('error_name_too_long');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('error_description_required');
    } else if (formData.description.length > GROUP_DESCRIPTION_MAX) {
      newErrors.description = t('error_description_too_long');
    }

    if (!imagePreview) {
      newErrors.image = t('error_image_required');
    }

    // No type is preselected for a new group; the owner must choose one (Architect, 4 Oct 2026).
    if (!GROUP_TYPE_OPTIONS.some(option => option.value === formData.group_access)) {
      newErrors.group_access = t('error_group_type_required');
    }

    // Validate hierarchy level (prevent Level 4 creation)
    if (selectedParentGroup && selectedParentGroup.hierarchy_level >= 3) {
      newErrors.parent = t('error_max_hierarchy_depth');
    }

    // Validate tree descendant limit (max 9 for free tier)
    if (selectedParentGroup && selectedParentGroup.is_full) {
      newErrors.parent = t('error_group_set_full', { max: MAX_TREE_DESCENDANTS_FREE });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAndExit = async (skipLocationWarning = false, skipUnlabelledFieldsWarning = false) => {
    addBreadcrumb('form-submit', { action: mode === 'edit' ? 'group-update' : 'group-create', component: 'AddEditGroup' });
    if (!validateForm()) {
      setModal({
        visible: true,
        message: t('error_fill_required_fields'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    // Double-check tree descendant limit (race condition protection)
    if (selectedParentGroup && mode === 'add') {
      const treeInfo = await getTreeDescendantCount(selectedParentGroup.id);
      if (treeInfo.pricing_tier === 'free' && treeInfo.descendant_count >= MAX_TREE_DESCENDANTS_FREE) {
        setModal({
          visible: true,
          message: t('error_parent_group_full', { max: MAX_TREE_DESCENDANTS_FREE }),
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
          }
        });
        return;
      }
    }

    // Warn if no location/area has been selected (non-blocking)
    if (skipLocationWarning !== true && selectedBoundaryIds.length === 0) {
      setModal({
        visible: true,
        title: t('warning_no_location_title'),
        message: t('warning_no_location_message'),
        okLabel: t('save_anyway'),
        cancelLabel: t('common:go_back'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          handleSaveAndExit(true, skipUnlabelledFieldsWarning);
        },
        onCancel: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // Warn before silently dropping any application-requirement row with no label — the
    // always-visible inline indicator on each row is the primary defence; this is the backstop
    // for a row added and never revisited before Save (Federation 4A fix).
    if (skipUnlabelledFieldsWarning !== true) {
      const unlabelledTypes = getUnlabelledRequirementTypes();
      if (unlabelledTypes.length > 0) {
        setModal({
          visible: true,
          title: t('warning_unlabelled_fields_title'),
          message: t('warning_unlabelled_fields_message', { types: unlabelledTypes.join(', ') }),
          okLabel: t('save_anyway'),
          cancelLabel: t('common:go_back'),
          danger: true,
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
            handleSaveAndExit(true, true);
          },
          onCancel: () => setModal({ visible: false, message: '', onOk: null })
        });
        return;
      }
    }

    setLoading(true);
    try {
      let coverImageUrl = formData.cover_image_url;

      // Drop unlabelled rows and trim — a field with no label is not a requirement yet.
      // `key` is left untouched: assigned once at add-time, never regenerated here.
      const normalizedFederationSchema = (formData.federation_application_schema || [])
        .filter(f => f.label && f.label.trim())
        .map(f => ({ key: f.key, label: f.label.trim(), type: f.type, required: !!f.required }));

      // The publication default (communities.admin_level) has never done anything and has left
      // the form until owner-curated content exists (Roadmap #91; addendum 1 item 2). It is not
      // written, so a saved group keeps its stored level untouched.

      const groupData = {
        name: formData.name,
        description: formData.description,
        community_type: 'group',
        postcode: formData.postcode || null,
        group_access: formData.group_access,
        is_private: formData.is_private,
        auto_accept_members: formData.auto_accept_members,
        cover_image_url: coverImageUrl,
        image_caption: formData.image_caption || null,
        logo_url: coverImageUrl, // Use same image for logo
        updated_at: new Date().toISOString(),
        // NEW - Image system fields (from ImageCropper)
        hero_image_display_url: groupImageDisplay || coverImageUrl || null,
        hero_image_thumbnail_url: groupImageThumbnail || null,
        // NEW - Nested Groups fields (hierarchy_level is auto-set by database trigger)
        parent_group_id: formData.parent_group_id || null,
        visible_to_parent: formData.parent_group_id ? formData.visible_to_parent : false,
        // NEW - Portugal administrative boundaries
        boundary_ids: formData.boundary_ids || [],
        // Federation
        is_federation: formData.is_federation || false,
        federation_application_schema: normalizedFederationSchema.length > 0 ? normalizedFederationSchema : null,
        // Contact
        show_contact_info: formData.show_contact_info || false,
        contact_details: formData.contact_details || null
      };

      const authToken = await getAuthToken();
      let data, error;
      let slug; // Hoisted so it's accessible after the if-else for groupId fallback

      if (mode === 'edit' && group && group.id) {
        // Update existing group (direct fetch)
        ({ data, error } = await supabaseFetch.update('communities', groupData, {
          eq: { id: group.id },
          authToken
        }));
      } else {
        // Create new group
        // Create slug from name with unique suffix to avoid conflicts
        const baseSlug = formData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        const uniqueSuffix = Date.now().toString(36).slice(-4);
        slug = `${baseSlug}-${uniqueSuffix}`;

        const newGroupData = {
          ...groupData,
          owner_id: userId,
          slug: slug,
          member_count: 1, // Owner is first member
          created_at: new Date().toISOString()
        };

        ({ data, error } = await supabaseFetch.insert('communities', newGroupData, {
          authToken
        }));

        if (!error && data) {
          // Add owner as first member (only for new groups)
          const { error: memberError } = await supabaseFetch.insert('community_members', {
            community_id: data.id,
            user_id: userId,
            role: 'owner',
            joined_at: new Date().toISOString()
          }, { authToken });

          if (memberError) {
            console.error('Error adding owner as member:', memberError);
          }

          // Note: Localities now handled via boundary_ids field (array in communities table)
        }
      }

      if (error) throw error;

      // Save tags (for both create and edit)
      let groupId = mode === 'edit' ? group.id : data?.id;

      // Fallback for new groups: if INSERT response didn't return the id (can happen
      // when Supabase RLS blocks the SELECT-back), query by slug to get the real id
      if (!groupId && mode !== 'edit' && slug) {
        const { data: createdGroup } = await supabaseFetch.query('communities', {
          select: 'id',
          eq: { slug },
          single: true
        });
        groupId = createdGroup?.id;
        console.log('📡 groupId fallback via slug lookup:', groupId);
      }

      if (groupId) {
        // Delete existing tags first (for edit mode)
        if (mode === 'edit') {
          const { error: deleteError } = await supabaseFetch.delete('community_tags', {
            eq: { community_id: groupId },
            authToken
          });
          if (deleteError) {
            console.warn('Warning: could not clear existing tags before re-saving:', deleteError);
          }
        }

        // Insert new tags
        if (formData.community_tags.length > 0) {
          const tagRecords = formData.community_tags.map(tag => ({
            community_id: groupId,
            tag: tag
          }));

          const { error: tagsError } = await supabaseFetch.insert('community_tags', tagRecords, { authToken });

          if (tagsError) {
            console.error('Error saving community tags:', tagsError);
            markSaved(); // the group itself was saved
            // Surface a non-fatal warning so the user knows tags may not have saved
            setModal({
              visible: true,
              message: t('warning_tags_not_saved', {
                action: mode === 'edit' ? t('updated') : t('created'),
                error: tagsError.message || tagsError
              }),
              onOk: () => {
                setModal({ visible: false, message: '', onOk: null });
                if (onBack) onBack();
              }
            });
            return;
          }
        }

        // ── Directory ↔ group link ──────────────────────────────────────────
        const link = pendingLinkRef.current;
        const listingSync = { name: formData.name, description: formData.description || null };
        if (formData.boundary_ids && formData.boundary_ids.length > 0) {
          listingSync.boundary_id = formData.boundary_ids[0];
        }
        if (mode === 'add' && link?.listingId) {
          // Task 1: stamp the linked listing with this group id + claim status
          // (verified when a Steward created it).
          await updateListing(link.listingId, {
            claimed_by_group_id: groupId,
            claim_status: link.claimStatus || 'pending'
          });
          pendingLinkRef.current = null;

          // Reconciliation: if the Steward edited the prefilled form so the group now diverges
          // from the source listing, defer to the per-field reconciliation modal. Otherwise the
          // group is the source of truth and we sync straight through below.
          const original = link.original || {};
          const diverged =
            (original.name || '') !== (formData.name || '') ||
            (original.description || '') !== (formData.description || '') ||
            // A national listing has no area (Roadmap #87): that is not a difference to reconcile.
            (original.boundary_id != null && original.boundary_id !== (listingSync.boundary_id ?? null));
          if (diverged) {
            setReconcileAfterCreate({
              listing: {
                id: link.listingId,
                name: original.name || '',
                description: original.description || '',
                boundary_id: original.boundary_id ?? null
              },
              group: {
                id: groupId,
                name: formData.name,
                description: formData.description,
                boundary_ids: formData.boundary_ids
              },
              groupName: formData.name
            });
            setLoading(false);
            return; // Success navigation resumes when the reconciliation modal completes.
          }
        }
        // Task 2: group → directory live sync (group is source of truth). Keys on
        // claimed_by_group_id, so it no-ops when no listing is linked. Shared fields only —
        // never contact / category / image. Boundary is synced only when the group has one,
        // since a linked listing that has an area must keep it; a national listing never takes
        // one (syncLinkedListing skips it). Non-atomic and RLS-gated
        // to Stewards: if the editor isn't the boundary Steward the card simply goes stale.
        await syncLinkedListing(groupId, listingSync);
        // ────────────────────────────────────────────────────────────────────
      } else {
        console.warn('Warning: groupId not available, tags were not saved');
      }

      // Show modal — on creation, navigate to admin page; on edit, go back
      markSaved();
      if (mode === 'edit') {
        setModal({
          visible: true,
          message: t('success_group_updated'),
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
            if (onBack) onBack();
          }
        });
      } else {
        navigateAfterCreate(groupId, formData.name);
      }
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} group:`, error);
      addCaughtError(error.message || String(error), 'AddEditGroup:save');
      const groupErrorMessage = error?.message?.includes('group_privacy_locked')
        ? t('privacy_locked_refused')
        : error?.message?.includes('federation_has_members')
        ? t('federation_undesignate_refused_members')
        : error?.message?.includes('federation_has_pending_applications')
        ? t('federation_undesignate_refused_pending')
        : isRateLimited(error)
        ? rateLimitMessage(error, t)
        : (mode === 'edit' ? t('error_failed_to_update') : t('error_failed_to_create'));
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: groupErrorMessage,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Save Draft — saves current state without requiring all fields
  // "Save and keep editing" (addendum 3 item 1; groups have no draft state, Roadmap #92). The name
  // handleSaveDraft is historical: it saves a live group and stays on the form (edit), or goes on
  // to the Introduction (create).
  const handleSaveDraft = async (skipUnlabelledFieldsWarning = false) => {
    addBreadcrumb('form-submit', { action: 'group-save-draft', component: 'AddEditGroup' });

    // Minimal validation: name is required (need something to identify the group)
    if (!formData.name.trim()) {
      setModal({
        visible: true,
        message: t('error_name_required'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // A type is required on every save, draft included: no type is preselected (Architect, 4 Oct
    // 2026), and the database refuses a group without one (communities_group_access_check).
    if (!GROUP_TYPE_OPTIONS.some(option => option.value === formData.group_access)) {
      setErrors(prev => ({ ...prev, group_access: t('error_group_type_required') }));
      setModal({
        visible: true,
        message: t('error_group_type_required'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // Validate length limits if fields are filled (but don't require them)
    if (formData.name.length > GROUP_NAME_MAX) {
      setModal({
        visible: true,
        message: t('error_name_too_long'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }
    if (formData.description.length > GROUP_DESCRIPTION_MAX) {
      setModal({
        visible: true,
        message: t('error_description_too_long'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // Warn before silently dropping any application-requirement row with no label — the
    // always-visible inline indicator on each row is the primary defence; this is the backstop
    // for a row added and never revisited before Save (Federation 4A fix).
    if (skipUnlabelledFieldsWarning !== true) {
      const unlabelledTypes = getUnlabelledRequirementTypes();
      if (unlabelledTypes.length > 0) {
        setModal({
          visible: true,
          title: t('warning_unlabelled_fields_title'),
          message: t('warning_unlabelled_fields_message', { types: unlabelledTypes.join(', ') }),
          okLabel: t('save_anyway'),
          cancelLabel: t('common:go_back'),
          danger: true,
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
            handleSaveDraft(true);
          },
          onCancel: () => setModal({ visible: false, message: '', onOk: null })
        });
        return;
      }
    }

    setLoading(true);
    try {
      let coverImageUrl = formData.cover_image_url;

      // Drop unlabelled rows and trim — a field with no label is not a requirement yet.
      // `key` is left untouched: assigned once at add-time, never regenerated here.
      const normalizedFederationSchema = (formData.federation_application_schema || [])
        .filter(f => f.label && f.label.trim())
        .map(f => ({ key: f.key, label: f.label.trim(), type: f.type, required: !!f.required }));

      // The publication default (communities.admin_level) has never done anything and has left
      // the form until owner-curated content exists (Roadmap #91; addendum 1 item 2). It is not
      // written, so a saved group keeps its stored level untouched.

      const groupData = {
        name: formData.name,
        description: formData.description,
        community_type: 'group',
        postcode: formData.postcode || null,
        group_access: formData.group_access,
        is_private: formData.is_private,
        auto_accept_members: formData.auto_accept_members,
        cover_image_url: coverImageUrl,
        image_caption: formData.image_caption || null,
        logo_url: coverImageUrl,
        updated_at: new Date().toISOString(),
        hero_image_display_url: groupImageDisplay || coverImageUrl || null,
        hero_image_thumbnail_url: groupImageThumbnail || null,
        parent_group_id: formData.parent_group_id || null,
        visible_to_parent: formData.parent_group_id ? formData.visible_to_parent : false,
        boundary_ids: formData.boundary_ids || [],
        is_federation: formData.is_federation || false,
        federation_application_schema: normalizedFederationSchema.length > 0 ? normalizedFederationSchema : null,
        show_contact_info: formData.show_contact_info || false,
        contact_details: formData.contact_details || null
      };

      const authToken = await getAuthToken();

      if (mode === 'edit' && group && group.id) {
        // Update existing group
        const { error } = await supabaseFetch.update('communities', groupData, {
          eq: { id: group.id },
          authToken
        });
        if (error) throw error;
      } else {
        // Create new group as draft
        const baseSlug = formData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        const uniqueSuffix = Date.now().toString(36).slice(-4);
        const slug = `${baseSlug}-${uniqueSuffix}`;

        const newGroupData = {
          ...groupData,
          owner_id: userId,
          slug: slug,
          member_count: 1,
          created_at: new Date().toISOString()
        };

        const { data, error } = await supabaseFetch.insert('communities', newGroupData, { authToken });
        if (error) throw error;

        if (data) {
          // Add owner as first member
          await supabaseFetch.insert('community_members', {
            community_id: data.id,
            user_id: userId,
            role: 'owner',
            joined_at: new Date().toISOString()
          }, { authToken });
          // A new group saved as a draft goes on to its Introduction like Save does (addendum 2
          // item 1): a private type opens the editor; Fully Public is offered it.
          navigateAfterCreate(data.id, formData.name);
          return;
        }
      }

      setModal({
        visible: true,
        message: t('success_saved_keep_editing'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } catch (error) {
      console.error('Error saving draft:', error);
      addCaughtError(error.message || String(error), 'AddEditGroup:saveDraft');
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: error?.message?.includes('group_privacy_locked')
          ? t('privacy_locked_refused')
          : error?.message?.includes('federation_has_members')
          ? t('federation_undesignate_refused_members')
          : error?.message?.includes('federation_has_pending_applications')
          ? t('federation_undesignate_refused_pending')
          : isRateLimited(error)
          ? rateLimitMessage(error, t)
          : (mode === 'edit' ? t('error_failed_to_update') : t('error_failed_to_create')),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setLoading(false);
    }
  };

  // Tag handlers
  const handleAddTag = () => {
    if (tagInput.trim()) {
      const newTags = tagInput
        .split(',')
        .map(tag => tag.trim().toUpperCase())
        .filter(tag => tag && !formData.community_tags.includes(tag));

      if (newTags.length > 0) {
        setFormData(prev => ({ ...prev, community_tags: [...prev.community_tags, ...newTags] }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      community_tags: prev.community_tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleCopyAIPrompt = () => {
    const prompt = generateGroupTagPrompt({
      ...formData,
      landingPageContent: landingPageContent
    });
    navigator.clipboard.writeText(prompt).then(() => {
      // Show success feedback
      setModal({
        visible: true,
        message: t('ai_prompt_copied'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }).catch(err => {
      console.error('Failed to copy prompt:', err);
      setModal({
        visible: true,
        message: t('error_copy_failed'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    });
  };

  // Cancel asks only when something changed (#78).
  const handleCancel = async () => {
    if ((await confirmLeave()) && onBack) onBack();
  };

  // Handle group soft-delete (owner only, edit mode only)
  const handleDeleteGroup = () => {
    if (mode !== 'edit' || !group?.id) return;

    setModal({
      visible: true,
      title: t('delete_group_title'),
      message: t('delete_group_confirm', { name: formData.name || group.name }) + '\n\n' +
        t('delete_effects_intro') + '\n' +
        t('delete_effect_schedule') + '\n' +
        t('delete_effect_subgroups') + '\n' +
        t('delete_effect_articles') + '\n' +
        t('delete_effect_notify') + '\n\n' +
        t('delete_undo_period'),
      okLabel: t('confirm_delete'),
      cancelLabel: t('common:cancel'),
      danger: true,
      onOk: async () => {
        setModal({ visible: false, message: '', onOk: null });
        setLoading(true);
        try {
          const result = await groupService.softDeleteGroup(group.id, userId);
          if (result.success) {
            markSaved(); // the group is scheduled for deletion; leaving must not ask
            const scheduledDate = new Date(result.deletion_scheduled_at).toLocaleDateString(formattingLocale);
            setModal({
              visible: true,
              title: t('delete_scheduled_title'),
              message: t('delete_scheduled_message', { name: result.group_name, date: scheduledDate }) +
                (result.children_deleted > 0
                  ? ' ' + t('delete_subgroups_count', { count: result.children_deleted })
                  : ''),
              okLabel: t('common:ok'),
              onOk: () => {
                setModal({ visible: false, message: '', onOk: null });
                if (onBack) onBack();
              }
            });
          } else {
            setModal({
              visible: true,
              title: t('common:error'),
              // A federation that groups belong to cannot be deleted (20260927g raises
              // federation_has_members); every other refusal keeps the generic message.
              message: String(result.error || '').includes('federation_has_members')
                ? t('delete_refused_federation_members')
                : t('error_delete_failed', { error: result.error }),
              okLabel: t('common:ok'),
              onOk: () => setModal({ visible: false, message: '', onOk: null })
            });
          }
        } catch (err) {
          console.error('Error deleting group:', err);
          setModal({
            visible: true,
            title: t('common:error'),
            message: t('error_delete_unexpected'),
            okLabel: t('common:ok'),
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        setModal({ visible: false, message: '', onOk: null });
      }
    });
  };

  // Recursive helper to render tree nodes as text-only hierarchy
  // Count all nodes in a tree (excluding root)
  const countTreeDescendants = (node) => {
    if (!node || !node.children) return 0;
    return node.children.reduce((sum, child) => sum + 1 + countTreeDescendants(child), 0);
  };

  // Collect all children including the "new group" placeholder into one list
  const getNodeChildren = (node, activeParentId, newGroupName) => {
    const realChildren = (node.children || []).map(child => ({ ...child, isPlaceholder: false }));
    if (node.id === activeParentId && mode === 'add') {
      realChildren.push({
        id: '__new__',
        name: newGroupName || t('placeholder_new_subgroup'),
        hierarchy_level: (node.hierarchy_level || 1) + 1,
        children: [],
        isPlaceholder: true
      });
    }
    return realChildren;
  };

  const renderTreeNode = (node, depth, activeParentId, currentGroupId, newGroupName, themeContext, isLast = true) => {
    if (!node) return null;

    const isCurrentGroup = node.id === currentGroupId;
    const isPlaceholder = node.isPlaceholder;
    const themeColor = themeContext?.primary || '#BB4D9A';
    const level = (node.hierarchy_level || depth + 1);
    const allChildren = getNodeChildren(node, activeParentId, newGroupName);
    const hasChildren = allChildren.length > 0;
    const connectorIndent = 12; // horizontal line length from vertical spine to node
    const nodeRowHeight = isCurrentGroup ? 42 : 28;

    return (
      <div key={node.id} style={{ position: 'relative' }}>
        {/* Horizontal connector from parent's vertical line to this node */}
        {depth > 0 && (
          <div style={{
            position: 'absolute',
            left: `-${connectorIndent}px`,
            top: `${nodeRowHeight / 2 + 4}px`,
            width: `${connectorIndent}px`,
            height: '2px',
            background: '#d1d5db'
          }} />
        )}
        {/* White mask to hide vertical line below last child's connector */}
        {depth > 0 && isLast && (
          <div style={{
            position: 'absolute',
            left: `-${connectorIndent}px`,
            top: `${nodeRowHeight / 2 + 4 + 2}px`,
            bottom: 0,
            width: '2px',
            background: 'white'
          }} />
        )}

        {/* The node content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minHeight: `${nodeRowHeight}px`,
          paddingTop: depth === 0 ? 0 : '4px',
          paddingBottom: '4px'
        }}>
          {/* Level badge */}
          <span style={{
            fontSize: '10px',
            fontWeight: '600',
            color: isCurrentGroup || isPlaceholder ? themeColor : '#9ca3af',
            backgroundColor: isCurrentGroup || isPlaceholder ? `${themeColor}15` : '#f3f4f6',
            padding: '1px 5px',
            borderRadius: '3px',
            flexShrink: 0
          }}>
            L{level}
          </span>

          {/* Current group: highlighted box. Others: plain text */}
          {isCurrentGroup ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: `${themeColor}15`,
              border: `2px solid ${themeColor}`,
              borderRadius: '8px',
              flex: 1,
              minWidth: 0
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '700',
                color: themeColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {node.name}
              </span>
              <span style={{
                fontSize: '10px',
                color: themeColor,
                opacity: 0.7,
                flexShrink: 0
              }}>
                {t('tree_editing')}
              </span>
            </div>
          ) : isPlaceholder ? (
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: themeColor,
              backgroundColor: `${themeColor}18`,
              padding: '4px 10px',
              borderRadius: '6px',
              border: `1px dashed ${themeColor}`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {node.name}
            </span>
          ) : (
            <span style={{
              fontSize: '13px',
              fontWeight: depth === 0 ? '600' : '400',
              color: '#374151',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {node.name}
            </span>
          )}
        </div>

        {/* Children with vertical connector line */}
        {hasChildren && (
          <div style={{
            position: 'relative',
            marginLeft: `${connectorIndent + 18}px`
          }}>
            {/* Vertical connector line — full height, trimmed by last child's mask */}
            <div style={{
              position: 'absolute',
              left: `-${connectorIndent}px`,
              top: 0,
              bottom: 0,
              width: '2px',
              background: '#d1d5db'
            }} />
            {allChildren.map((child, idx) =>
              renderTreeNode(child, depth + 1, activeParentId, currentGroupId, newGroupName, themeContext, idx === allChildren.length - 1)
            )}
          </div>
        )}
      </div>
    );
  };

  if (mode === 'edit' && closingState.closing) {
    return (
      <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
        <HeaderContainer />
        <HeaderButtonsForm currentContext={headerContext} onBack={onBack} headerTitle={t('add_edit_group')} />
        <div style={{ paddingTop: '100px', paddingLeft: '40px', paddingRight: '40px' }}>
          <GroupClosingBanner
            closing
            closesOn={closingState.closesOn}
            variant="admin"
            canUndo={closingState.canUndo}
            undoing={closingState.undoing}
            undoFailed={closingState.undoFailed}
            onUndo={closingState.undo}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
      {/* Header */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={headerContext}
        onBack={onBack}
        headerTitle={t('add_edit_group')}
        onCheckUnsaved={confirmLeave}
      />

      {/* Main Content */}
      <div className="flex-1 pt-[90px]" style={{ paddingBottom: isMobile ? '120px' : '220px' }}>
        <div className="max-w-[1600px]" style={{ marginTop: '0px', marginLeft: isMobile ? '16px' : '40px', marginRight: isMobile ? '16px' : '0px' }}>
          <div className={isMobile ? 'flex flex-col gap-6' : 'flex gap-8'}>
            {/* Left Column - Fixed 480px on desktop, full width on mobile - shows SECOND on mobile */}
            <div className="space-y-3" style={isMobile ? { width: '100%', order: 2 } : { width: '480px', flexShrink: 0 }}>
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_group_image')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-white" style={isMobile ? { width: '100%', aspectRatio: '4 / 3' } : { width: '448px', height: '336px' }}>
                  {imagePreview ? (
                    <div className="relative h-full">
                      <img
                        src={imagePreview}
                        alt={t('alt_group_image_preview')}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => setShowGroupCropper(true)}
                        className="absolute top-2 right-2 bg-[var(--theme-primary)] text-white px-3 py-1 rounded-lg text-sm hover:bg-[#9a3d7e]"
                      >
                        {t('button_change')}
                      </button>
                      <button
                        onClick={() => {
                          setImagePreview(null);
                          setGroupImageDisplay('');
                          setGroupImageThumbnail('');
                          setFormData(prev => ({ ...prev, cover_image_url: '' }));
                        }}
                        className="absolute top-2 right-20 bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600"
                      >
                        {t('button_remove')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Upload className="mb-2 text-[var(--theme-primary)]" size={48} />
                      <p className="text-[var(--theme-primary)] font-semibold mb-2 text-sm">
                        {t('upload_select_image')}<br />{t('upload_from_device')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowGroupCropper(true)}
                        className="inline-block px-6 py-2 bg-[var(--theme-primary)] text-white rounded-lg cursor-pointer hover:bg-[#9a3d7e] transition-colors"
                      >
                        {t('button_choose_image')}
                      </button>
                      <p className="text-xs text-gray-600 mt-2">
                        {t('hint_image_ratio')}
                      </p>
                    </div>
                  )}
                  {errors.image && (
                    <p className="text-red-500 text-sm mt-2">{errors.image}</p>
                  )}
                </div>

                {/* Image Caption */}
                {imagePreview && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('field_photo_credit')}
                    </label>
                    <input
                      type="text"
                      value={formData.image_caption || ''}
                      onChange={(e) => handleInputChange('image_caption', e.target.value)}
                      placeholder={t('placeholder_photo_credit')}
                      maxLength={ARTICLE_IMAGE_CAPTION_MAX}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)] text-sm"
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      {(formData.image_caption || '').length}/100 {t('common:characters')}
                    </p>
                  </div>
                )}
              </div>

              {/* Group Hierarchy Tree - shows full organisation for any group in a hierarchy */}
              {(selectedParentGroup || loadingGroupTree || (groupTree && countTreeDescendants(groupTree) > 0)) && (
                <div style={{ marginTop: '20px', width: isMobile ? '100%' : '448px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    {t('section_group_organisation')}
                  </label>

                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '16px',
                    position: 'relative'
                  }}>
                    {loadingGroupTree ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                        {t('loading_tree')}
                      </div>
                    ) : groupTree ? (
                      <div style={{ fontFamily: 'inherit' }}>
                        {/* Tree descendant count header */}
                        {(() => {
                          const descendantCount = countTreeDescendants(groupTree);
                          const tier = selectedParentGroup?.pricing_tier || 'free';
                          const isFree = tier === 'free';
                          return (
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px',
                              paddingBottom: '10px',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                {t('tree_subgroups_used')}
                              </span>
                              <span style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: isFree && descendantCount >= MAX_TREE_DESCENDANTS_FREE ? '#ef4444' : '#374151'
                              }}>
                                {isFree ? `${descendantCount} / ${MAX_TREE_DESCENDANTS_FREE}` : `${descendantCount} (${t('tree_unlimited')})`}
                              </span>
                            </div>
                          );
                        })()}
                        {/* Render tree recursively */}
                        {renderTreeNode(groupTree, 0, selectedParentGroup?.id, group?.id, formData.name, theme)}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                        {t('tree_no_data')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Federation Members Section - Only in edit mode for federation groups */}
              {mode === 'edit' && group && formData.is_federation && (
                <div style={{ marginTop: '24px', width: '448px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Layers size={18} color={theme?.primary || '#BB4D9A'} />
                      {t('federation_members')}
                    </h3>
                  </div>

                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '12px',
                    minHeight: '60px'
                  }}>
                    {loadingFedMembers ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                        {t('loading_federation_members')}
                      </div>
                    ) : fedMembers.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                        {t('no_federation_members_yet')}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {fedMembers.map(member => (
                          <div
                            key={member.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px',
                              background: '#f9fafb',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('ournet:navigate', {
                                detail: { view: 'admin-group', group: member }
                              }));
                            }}
                          >
                            {member.logo_url ? (
                              <img
                                src={member.logo_url}
                                alt={member.name}
                                style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '6px',
                                background: '#e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Layers size={16} color="#9ca3af" />
                              </div>
                            )}
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>
                                {member.name}
                              </p>
                              <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                                {member.member_count || 0} {t('members_count', { count: member.member_count || 0 })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Application Requirements Section - Only in edit mode for federation groups.
                  Defines communities.federation_application_schema: what a prospective Federee
                  must supply. The applicant-facing form that reads this is a later phase (4B). */}
              {mode === 'edit' && group && formData.is_federation && (
                <div style={{ marginTop: '24px', width: '448px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <ClipboardList size={18} color={theme?.primary || '#BB4D9A'} />
                      {t('section_application_requirements')}
                    </h3>
                  </div>
                  <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                    {t('application_requirements_description')}
                  </p>

                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '12px'
                  }}>
                    {(formData.federation_application_schema || []).length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#9ca3af', fontSize: '13px' }}>
                        {t('application_requirements_empty')}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                        {formData.federation_application_schema.map((field) => (
                          <div
                            key={field.key}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                              padding: '10px',
                              background: '#f9fafb',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <select
                                value={field.type || 'text'}
                                onChange={(e) => handleRequirementTypeChange(field.key, e.target.value)}
                                style={{
                                  padding: '6px 8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  flexShrink: 0
                                }}
                              >
                                <option value="text">{t('field_type_text')}</option>
                                <option value="longtext">{t('field_type_longtext')}</option>
                                <option value="email">{t('field_type_email')}</option>
                                <option value="phone">{t('field_type_phone')}</option>
                              </select>
                              <input
                                type="text"
                                value={field.label || ''}
                                onChange={(e) => handleUpdateRequirementField(field.key, { label: e.target.value })}
                                placeholder={t('requirement_label_placeholder')}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  border: `1px solid ${(field.label || '').trim() ? '#d1d5db' : '#ef4444'}`,
                                  borderRadius: '6px',
                                  fontSize: '13px'
                                }}
                              />
                              <StandardButton
                                variant="inline"
                                iconName="Trash2"
                                onClick={() => handleRemoveRequirementField(field.key)}
                                title={t('remove_requirement')}
                              />
                            </div>
                            {!(field.label || '').trim() && (
                              <p style={{ color: '#ef4444', fontSize: '11px', margin: 0 }}>
                                {t('requirement_needs_label')}
                              </p>
                            )}
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '12px',
                                color: '#374151',
                                cursor: 'pointer'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={!!field.required}
                                  onChange={(e) => handleUpdateRequirementField(field.key, { required: e.target.checked })}
                                  style={{ width: '14px', height: '14px' }}
                                />
                                {t('requirement_required_label')}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <StandardButton
                      variant="inline"
                      iconName="Plus"
                      label={t('button_add_requirement')}
                      onClick={handleAddRequirementField}
                    />
                  </div>
                </div>
              )}

              {/* Sub-Groups Section - Only in edit mode for groups that can have children */}
              {mode === 'edit' && group && currentGroupHierarchyLevel < 3 && (
                <div style={{ marginTop: '24px', width: '448px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Layers size={18} color={theme?.primary || '#BB4D9A'} />
                      {t('section_subgroups')}
                    </h3>
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('ournet:navigate', {
                          detail: {
                            view: 'new-group',
                            mode: 'add',
                            parentGroup: {
                              id: group.id,
                              name: formData.name,
                              hierarchy_level: currentGroupHierarchyLevel,
                              logo_url: imagePreview || formData.cover_image_url
                            }
                          }
                        }));
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        background: theme?.primary || '#BB4D9A',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} />
                      {t('button_create_subgroup')}
                    </button>
                  </div>

                  {/* List of existing sub-groups */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '12px',
                    minHeight: '100px'
                  }}>
                    {loadingChildren ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                        {t('loading_subgroups')}
                      </div>
                    ) : childGroups.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                        {t('no_subgroups_yet')}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {childGroups.map(child => (
                          <div
                            key={child.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px',
                              background: '#f9fafb',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('ournet:navigate', {
                                detail: { view: 'admin-group', group: child }
                              }));
                            }}
                          >
                            {child.logo_url ? (
                              <img
                                src={child.logo_url}
                                alt={child.name}
                                style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '6px',
                                background: '#e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Layers size={16} color="#9ca3af" />
                              </div>
                            )}
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>
                                {child.name}
                              </p>
                              <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                                {t('tree_level')} {child.hierarchy_level} • {child.member_count || 0} {t('members_count', { count: child.member_count || 0 })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Middle Column - Flexible - shows FIRST on mobile */}
            <div className="space-y-3 flex-1" style={isMobile ? { order: 1 } : {}}>
              {/* Parent Group Name - shown above Name when creating a subgroup */}
              {selectedParentGroup && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: theme?.primary || '#BB4D9A',
                  borderRadius: '8px',
                  marginBottom: '4px'
                }}>
                  {selectedParentGroup.logo_url && (
                    <img
                      src={selectedParentGroup.logo_url}
                      alt={selectedParentGroup.name}
                      style={{ width: '28px', height: '28px', borderRadius: '4px', objectFit: 'cover' }}
                    />
                  )}
                  <span style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>
                    {selectedParentGroup.name}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginLeft: 'auto' }}>
                    {t('tree_level')} {selectedParentGroup.hierarchy_level}
                  </span>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {selectedParentGroup ? t('field_subgroup_name') : t('field_name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('placeholder_name')}
                  maxLength={GROUP_NAME_MAX}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)]`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {formData.name.length}/{GROUP_NAME_MAX} {t('common:characters')}
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('placeholder_description')}
                  maxLength={GROUP_DESCRIPTION_MAX}
                  rows={4}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)] resize-none`}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {formData.description.length}/{GROUP_DESCRIPTION_MAX} {t('common:characters')}
                </p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_tags')}
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  {t('tags_instructions')}
                </p>

                {/* Input Area */}
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder={t('placeholder_tags')}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="bg-[var(--theme-primary)] text-white px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity"
                  >
                    {t('button_add')}
                  </button>
                </div>

                {/* AI Prompt Helper */}
                <div className="mb-4 flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <button
                    type="button"
                    onClick={handleCopyAIPrompt}
                    className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
                  >
                    {t('button_copy_ai_prompt')}
                  </button>
                  <p className="text-xs text-gray-600 flex-1">
                    {t('ai_prompt_hint')}
                  </p>
                </div>

                {/* Tag Pills Display Area */}
                <div className="bg-white rounded-lg p-4 min-h-[120px] border border-gray-300">
                  <div className="flex flex-wrap gap-2">
                    {formData.community_tags.map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="bg-teal-500 text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-teal-600 transition-colors text-sm font-medium"
                      >
                        <span>{tag}</span>
                        <span className="text-lg leading-none">×</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Parent Group Selector - Only in 'add' mode when not already from Create Sub-Group flow */}
              {mode === 'add' && !parentGroup && (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    {t('field_parent_group')}
                  </label>
                  <p style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    {t('hint_parent_group')}
                  </p>

                  {/* Custom Dropdown */}
                  <div style={{ position: 'relative' }}>
                    {/* Dropdown Trigger */}
                    <div
                      onClick={() => !loadingEligibleParents && setParentSelectorOpen(!parentSelectorOpen)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: loadingEligibleParents ? 'wait' : 'pointer',
                        minHeight: '48px'
                      }}
                    >
                      {loadingEligibleParents ? (
                        <span style={{ color: '#9ca3af', fontSize: '14px' }}>{t('loading_parent_options')}</span>
                      ) : selectedParentGroup ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          {selectedParentGroup.logo_url ? (
                            <img
                              src={selectedParentGroup.logo_url}
                              alt={selectedParentGroup.name}
                              style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              background: '#e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Layers size={16} color="#9ca3af" />
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>
                              {selectedParentGroup.name}
                            </p>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                              {t('level')} {selectedParentGroup.hierarchy_level} • {selectedParentGroup.tree_descendant_count || 0}/{selectedParentGroup.pricing_tier === 'free' ? MAX_TREE_DESCENDANTS_FREE : '∞'} {t('in_group_set')}
                            </p>
                          </div>
                          {/* Clear button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearParent();
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: '4px',
                              cursor: 'pointer',
                              borderRadius: '4px'
                            }}
                          >
                            <X size={18} color="#9ca3af" />
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                          {t('none_top_level')}
                        </span>
                      )}
                      <ChevronDown
                        size={20}
                        color="#6b7280"
                        style={{
                          transform: parentSelectorOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                          marginLeft: selectedParentGroup ? '0' : '8px'
                        }}
                      />
                    </div>

                    {/* Dropdown Options */}
                    {parentSelectorOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 50,
                        maxHeight: '280px',
                        overflowY: 'auto'
                      }}>
                        {/* None option */}
                        <div
                          onClick={() => handleParentGroupSelect(null)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            background: !selectedParentGroup ? '#f3f4f6' : 'white',
                            borderBottom: '1px solid #e5e7eb'
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <span style={{ fontSize: '18px', color: '#9ca3af' }}>-</span>
                          </div>
                          <div>
                            <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151', margin: 0 }}>
                              {t('none_top_level')}
                            </p>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                              {t('create_independent')}
                            </p>
                          </div>
                        </div>

                        {/* Eligible parent groups */}
                        {eligibleParentGroups.length === 0 ? (
                          <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                            {t('no_eligible_parents')}
                          </div>
                        ) : (
                          eligibleParentGroups.map(grp => (
                            <div
                              key={grp.id}
                              onClick={() => !grp.is_full && handleParentGroupSelect(grp.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                cursor: grp.is_full ? 'not-allowed' : 'pointer',
                                background: selectedParentGroup?.id === grp.id ? '#fdf2f8' : 'white',
                                borderBottom: '1px solid #f3f4f6',
                                opacity: grp.is_full ? 0.5 : 1
                              }}
                            >
                              {grp.logo_url ? (
                                <img
                                  src={grp.logo_url}
                                  alt={grp.name}
                                  style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '6px',
                                  background: '#e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <Layers size={16} color="#9ca3af" />
                                </div>
                              )}
                              <div style={{ flex: 1 }}>
                                <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151', margin: 0 }}>
                                  {grp.name}
                                </p>
                                <p style={{ fontSize: '11px', color: grp.is_full ? '#ef4444' : '#6b7280', margin: 0 }}>
                                  {t('level')} {grp.hierarchy_level} • {grp.tree_descendant_count}/{grp.pricing_tier === 'free' ? MAX_TREE_DESCENDANTS_FREE : '∞'} {t('in_group_set')}
                                  {grp.is_full && ` (${t('full')})`}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Click outside to close */}
                  {parentSelectorOpen && (
                    <div
                      onClick={() => setParentSelectorOpen(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 40
                      }}
                    />
                  )}
                </div>
              )}

              {/* Visible to Parent Toggle - Only for subgroups */}
              {selectedParentGroup && (
                <div className="mt-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.visible_to_parent}
                      onChange={(e) => handleInputChange('visible_to_parent', e.target.checked)}
                      className="w-5 h-5 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)] mr-3"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-700">
                        {t('visible_to_parent')}
                      </span>
                      <p className="text-xs text-gray-500">
                        {t('visible_to_parent_hint', { name: selectedParentGroup.name })}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Free Tier Notice */}
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '12px'
              }}>
                <p style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#F9A61B',
                  textAlign: 'center',
                  margin: '0 0 10px 0'
                }}>
                  {t('free_forever')}
                </p>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>
                  {/* dangerouslySetInnerHTML here is safe: content is translation strings only, never user input */}
                  <p style={{ margin: '0 0 4px 0' }} dangerouslySetInnerHTML={{ __html: '• ' + t('free_limit_members') }} />
                  <p style={{ margin: '0 0 4px 0' }} dangerouslySetInnerHTML={{ __html: '• ' + t('free_limit_subgroups', { max: MAX_TREE_DESCENDANTS_FREE }) }} />
                  <p style={{ margin: 0, color: '#9ca3af', fontStyle: 'italic' }}>{t('free_limit_upgrade')}</p>
                </div>
              </div>

            </div>

            {/* Right Column - Fixed 300px on desktop, full width on mobile - shows THIRD on mobile */}
            <div style={isMobile ? { width: '100%', order: 3 } : { width: '300px', flexShrink: 0 }}>
              {/* Group Type - Compact */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  {t('group_type')}
                </label>
                {/* A Public group that still approves new members (addendum 1 item 5): it must choose,
                    and saving as Fully Public opens it — said here before any save. */}
                {legacyApproval && (
                  <p style={{ fontSize: '12px', color: tokens.colors.textSecondary, background: 'white', border: `1px solid ${tokens.colors.help}`, borderRadius: '6px', padding: '6px 8px', margin: '0 0 6px' }}>
                    {t('legacy_approval_notice')}
                  </p>
                )}
                <div style={{ background: 'white', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 10px' }}>
                  {GROUP_TYPE_OPTIONS.map((option, index) => {
                    const locked = isTypeLocked(option.value);
                    return (
                      <label
                        key={option.value}
                        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', cursor: locked ? 'not-allowed' : 'pointer', marginBottom: index < GROUP_TYPE_OPTIONS.length - 1 ? '8px' : 0 }}
                      >
                        <div>
                          <span style={{ color: locked ? tokens.colors.disabledText : (theme?.primary || '#BB4D9A'), fontWeight: '600', fontSize: '13px' }}>{t(option.labelKey)}</span>
                          <span style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{t(option.hintKey)}</span>
                        </div>
                        <input
                          type="radio"
                          name="group_access"
                          value={option.value}
                          checked={formData.group_access === option.value}
                          onChange={(e) => handleGroupTypeChange(e.target.value)}
                          disabled={locked}
                          style={{ width: '16px', height: '16px', flexShrink: 0, marginTop: '2px' }}
                        />
                      </label>
                    );
                  })}
                </div>
                {errors.group_access && (
                  <p className="text-red-500 text-sm mt-1">{errors.group_access}</p>
                )}
                {/* What comes after saving a new group (addendum 1 item 4). Beside Preview on desktop
                    (footer); here on mobile, where the footer has no room for a line. */}
                {isMobile && introNextLine && (
                  <p style={{ fontSize: '11px', color: tokens.colors.textSecondary, margin: '6px 0 0' }}>
                    {introNextLine}
                  </p>
                )}
                {privacyLocked && (
                  <p style={{ fontSize: '11px', color: tokens.colors.textSecondary, margin: '6px 0 0' }}>
                    {t('privacy_locked_notice')}
                  </p>
                )}
              </div>

              {/* Federation Toggle */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '10px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.is_federation}
                      onChange={(e) => handleFederationToggle(e.target.checked)}
                      style={{ width: '16px', height: '16px', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div>
                      <span style={{ color: theme?.primary || '#BB4D9A', fontWeight: '600', fontSize: '13px' }}>
                        {t('federation')}
                      </span>
                      <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>
                        {t('federation_description')}
                      </p>
                    </div>
                  </label>
                  {errors.is_federation && (
                    <p style={{ color: '#ef4444', fontSize: '11px', margin: '8px 0 0 26px' }}>
                      {errors.is_federation}
                    </p>
                  )}
                </div>
              </div>

              {/* Contact Information Toggle + Details */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '10px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.show_contact_info}
                      onChange={(e) => handleInputChange('show_contact_info', e.target.checked)}
                      style={{ width: '16px', height: '16px', flexShrink: 0 }}
                    />
                    <span style={{ color: theme?.primary || '#BB4D9A', fontWeight: '600', fontSize: '13px' }}>
                      {t('show_contact_info')}
                    </span>
                  </label>
                  {formData.show_contact_info && (
                    <textarea
                      value={formData.contact_details}
                      onChange={(e) => handleInputChange('contact_details', e.target.value)}
                      placeholder={t('contact_details_placeholder')}
                      rows={3}
                      style={{
                        width: '100%',
                        marginTop: '8px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        resize: 'vertical',
                        fontFamily: 'OpenSauceSans, sans-serif',
                        lineHeight: '1.4'
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Location Selector - Portugal Boundaries */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                      {t('select_areas')}
                    </label>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0' }}>
                      {t('select_areas_description')}
                    </p>
                  </div>
                  <LanguageSelector />
                </div>
                <UnifiedLocationSelector
                  mode="group"
                  allowBoundary={true}
                  allowPin={false}
                  multiSelect={true}
                  initialBoundaries={selectedBoundaryIds}
                  initialCountry="PT"
                  onBoundaryChange={(ids, boundaries) => {
                    setSelectedBoundaryIds(ids);
                    setFormData(prev => ({ ...prev, boundary_ids: ids }));
                  }}
                  compact={false}
                  userLongitude={userLongitude}
                  // 2026-08-26 save-and-exit/parity build, Part 2: deliberately a no-op — this
                  // form's boundary selection is already local state only (onBoundaryChange above
                  // syncs it into formData on every click; nothing is written to the DB until the
                  // group's own footer "Save and Exit"/"Create and Exit" submit, handleSaveAndExit
                  // at ~line 618). Triggering THAT whole-form submit from here would materially
                  // change what a small, crosshair-scoped action does (and its destination would
                  // stop being "back to group setup," contradicting the brief). Advisor STRONGLY
                  // ADVISEd against reusing the "Save and exit" label here specifically, since the
                  // footer already uses that exact wording for the real, whole-form commit — a
                  // second, differently-scoped button with the same label risks a user believing
                  // their edits are saved and leaving, losing them. saveAndExitLabel below is
                  // deliberately distinct wording for that reason.
                  onSaveAndExit={() => {}}
                  saveAndExitLabel={t('crosshair_area_confirmed_label')}
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <FooterContainer viewType="form" isMobile={isMobile}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '0 8px' : '0 40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '16px'
          }}>
            {!isMobile && introNextLine && (
              <span style={{ fontSize: '12px', color: tokens.colors.textSecondary, maxWidth: '260px' }}>
                {introNextLine}
              </span>
            )}

            {/* Preview Button */}
            <StandardButton
              variant="utility"
              iconName="Eye"
              label={t('preview')}
              onClick={() => setShowPreview(true)}
            />

            {/* The group's Introduction (its landing page) — beside Preview on Edit Group (addendum
                1 item 4). The same editor the post-create handoff opens; no second editor. */}
            {mode === 'edit' && group && group.id && (
              <StandardButton
                variant="utility"
                iconName="FileText"
                label={t('edit_landing_page')}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('ournet:navigate', {
                    detail: {
                      view: 'group-landing',
                      group: {
                        id: group.id,
                        name: formData.name,
                        cover_image_url: imagePreview || formData.cover_image_url
                      }
                    }
                  }));
                }}
              />
            )}

            {/* Save Draft */}
            <StandardButton
              variant="utility"
              iconName="Save"
              label={loading ? t('saving') : t('save_and_keep_editing')}
              onClick={handleSaveDraft}
              disabled={loading}
            />

            {/* Cancel */}
            <StandardButton
              variant="destructive"
              iconName="X"
              label={t('common:cancel')}
              onClick={handleCancel}
              confirmBefore={false}
            />

            {/* Delete Group - Edit mode only, owner only */}
            {mode === 'edit' && groupOwnerId === userId && (
              <StandardButton
                variant="destructive"
                iconName="Trash2"
                label={isMobile ? t('delete') : t('delete_group')}
                onClick={handleDeleteGroup}
                disabled={loading}
                confirmBefore={false}
              />
            )}

            {/* Create and Exit / Save and Exit */}
            <StandardButton
              variant="action"
              iconName={mode === 'edit' ? 'Save' : 'Send'}
              label={loading ? t('saving') : (mode === 'edit' ? t('save_and_exit') : t('create_and_exit'))}
              onClick={() => handleSaveAndExit()}
              disabled={loading}
            />
          </div>
        </div>
      </FooterContainer>

      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('common:confirmation')}
        message={modal.message}
        okLabel={modal.okLabel}
        cancelLabel={modal.cancelLabel}
        danger={modal.danger}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); }
        }}
        onCancel={() => {
          try {
            if (modal.onCancel) modal.onCancel();
            else setModal({ visible: false, message: '', onOk: null });
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
          }
        }}
      />

      {/* Create-from-prefill reconciliation: the group is already created and linked; the modal
          resolves any diverging shared fields, then we complete the create-success navigation.
          Cancel keeps the link as-is (listing retains its values) and still finishes the flow. */}
      {reconcileAfterCreate && (
        <ReconcileLinkModal
          listing={reconcileAfterCreate.listing}
          group={reconcileAfterCreate.group}
          onClose={() => {
            const { group, groupName } = reconcileAfterCreate;
            setReconcileAfterCreate(null);
            navigateAfterCreate(group.id, groupName);
          }}
          onDone={() => {
            const { group, groupName } = reconcileAfterCreate;
            setReconcileAfterCreate(null);
            navigateAfterCreate(group.id, groupName);
          }}
        />
      )}

      {/* NEW - Group Image Cropper Modal */}
      <ImageCropper
        isOpen={showGroupCropper}
        onClose={() => setShowGroupCropper(false)}
        onSave={handleGroupImageSave}
        aspectMode="hero"
        bucket="hero-images"
        userId={userId}
      />

      {/* Group Preview Modal - no navigation, preserves form state */}
      <GroupPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        formData={formData}
        imagePreview={imagePreview || formData.cover_image_url || null}
      />
    </div>
  );
};

export default AddEditGroup;
